import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CouponResponse, CouponViewModel, EstimateResponse, LineItem, LOADING_TYPE, EstimationRequest, TABS_TYPE } from 'src/app/domains/payment/models/payment.model';
import { PaymentService } from 'src/app/domains/payment/services/payment.service';

@Component({
  selector: 'gdp-payment-info',
  templateUrl: './payment-info.component.html',
  styleUrls: ['./payment-info.component.scss']
})
export class PaymentInfoComponent implements OnInit {
  @Input() product: EstimationRequest;
  @Input() country: string;
  @Input() chargebeeAccount: string;
  @Input() companyId: string;
  @Input() canModifyCoupon: boolean = true;
  @Input() didId: number;
  public estimate: EstimateResponse;
  public loading = {
    box: false,
    lines: false
  };
  public items: LineItem = {
    immediate: {
      recurrent: { plans: [], addons: [] },
      one_time:  { addons: [] },
      estimation: null
    },
    renewal: {
      recurrent: { plans: [], addons: [] },
      one_time:  { addons: [] },
      estimation: null
    }
  };
  // to manage errors
  public error = false;
  public errorExpired = false;
  public errorConflict = null;
  public showPreviousCredits: boolean = false;
  public previousCredits: number = 0;
  // to manage coupon
  public coupons: {
    immediate: Array<CouponViewModel>
    renewal: Array<CouponViewModel>
  } = {immediate: [], renewal: []};
  public couponOneTime: boolean = false;
  public couponExist: boolean = false;
  public isCouponChanged = {
    immediate: false,
    renewal: false
  };
  // to manage tabs
  public showTabs: boolean = true;
  public TABS_TYPE = TABS_TYPE;
  public selectedTab: TABS_TYPE = TABS_TYPE.immediate;
  // output
  @Output() onChangeEstimate = new EventEmitter<EstimationRequest>();
  @Output() onLoadingEstimation = new EventEmitter<boolean>();
  @Output() onEstimateResponse = new EventEmitter<any>();
  @Output() onEstimateError = new EventEmitter<boolean>();

  constructor(private paymentService: PaymentService) { }

  ngOnInit(): void {
    if(this.product.coupon_id) {
      this.couponExist = true;
    }
    this.getEstimate(LOADING_TYPE.box);
  }

  onSelectTab(event): void {
    this.selectedTab = event.nextId;
    if(this.selectedTab === TABS_TYPE.renewal && (this.isRenewalTabEmpty() || this.isCouponChanged.renewal )) {
      this.product = {
        ...this.product,
        end_of_term: true
      };
      if(this.couponOneTime) {
        this.product.coupon_id = null;
      }
      this.isCouponChanged.renewal = false;
      this.getEstimate(LOADING_TYPE.lines);
    } else if(this.selectedTab === TABS_TYPE.immediate && this.isCouponChanged.immediate) {
      this.product = {
        ...this.product,
        end_of_term: false
      };
      this.isCouponChanged.immediate = false;
      this.getEstimate(LOADING_TYPE.lines);
    }
  }

  private isRenewalTabEmpty(): boolean {
    return this.items.renewal.one_time.addons.length === 0 &&
    this.items.renewal.recurrent.addons.length === 0 &&
    this.items.renewal.recurrent.plans.length === 0;
  }

  resetItems() {
    this.items[this.selectedTab] = {
      recurrent: {
        plans: [],
        addons: []
      },
      one_time: {
        addons: []
      },
      estimation: null
    };
  }

  private getEstimate(loading?: string, coupon?: CouponResponse) {
    this.onEstimateError.emit(false);
    if (coupon) {
      this.couponExist = true;
      this.product.coupon_id = coupon.id;
    }
    this.fetchEstimate();
    this.loading[loading] = true;
    this.error = this.errorConflict = null;
  }

  private fetchEstimate() {
    this.resetItems();
    this.product = this.didId ? {...this.product, did_id: this.didId} : {...this.product};
    this.paymentService.getEstimate(this.companyId ,this.product)
      .subscribe(r => {
        this.estimate = r;
        this.items[this.selectedTab].estimation = this.estimate;
        this.loading = {
          box: false,
          lines: false
        };
        this.buildLineItems();
        if(this.estimate.line_items.every(item => item.entity_type === 'one_time_charge')) {
          this.showTabs = false;
        } else {
          this.showTabs = true;
        }
        this.onLoadingEstimation.emit(this.loading.box || this.loading.lines);
        this.onChangeEstimate.emit(this.product);
        this.onEstimateResponse.emit(this.estimate);
        this.onEstimateError.emit(false);
        this.getCouponInfo();
      }, err => {
        if (err.status === 409) {
          if (err.error.message.includes('too many')) {
            this.errorConflict = 'payment-info.errors.too-many.' + this.product.product.split('_')[0];
          } else {
            this.errorConflict = err.error.message;
          }
        } else {
          this.errorConflict = 'payment-info.errors.general';
        }
        this.estimate = null;
        this.loading = {
          box: false,
          lines: false
        };
        this.onLoadingEstimation.emit(this.loading.box || this.loading.lines);
        this.onEstimateError.emit(true);
      });
  }

  private buildLineItems(): void {
    this.estimate.line_items.forEach((el: any) => {
      switch (el.entity_type) {
        case 'plan':
          {
            this.items[this.selectedTab].recurrent.plans.push(el);
          }
          break;
        case 'addon':
          {
            this.items[this.selectedTab].recurrent.addons.push(el);
          }
          break;
        case 'one_time_charge':
          this.items[this.selectedTab].one_time.addons.push(el);
          break;
        default:
          break;
      }
    });
  }

  private getCouponInfo() {
    if(this.estimate.discount_items.length === 0) {
      this.couponExist = false;
      return;
    } else {
      this.estimate.discount_items.forEach(el => {
        if (!el.coupon_id) {
          this.couponExist = false;
          return;
        }
        this.paymentService.getCouponInfo(el.coupon_id, this.chargebeeAccount).subscribe(res => {
          if (el.coupon_id === res.id) {
            if (res.couponDuration === 'one_time') {
              this.couponOneTime = true;
            }
            this.coupons[this.selectedTab].push({
              id: res.id,
              name: res.name,
              duration: res.couponDuration,
              type: el.discount_type,
              amount: el.amount
            });
          }
        });
      });
    }
  }

  public removeCoupon(index: number) {
    if (this.coupons[this.selectedTab][index].duration === 'one_time') {
      this.couponOneTime = false;
    }
    this.couponExist = false;
    this.coupons.immediate.splice(index, 1);
    this.coupons.renewal.splice(index, 1);
    this.product.coupon_id = null;
    if(this.selectedTab === TABS_TYPE.immediate) {
      this.product.end_of_term = false;
      this.isCouponChanged = {
        immediate: false,
        renewal: true
      };
    } else {
      this.product.end_of_term = true;
      this.isCouponChanged = {
        immediate: true,
        renewal: false
      };
    }
    this.getEstimate(LOADING_TYPE.lines);
  }

  couponChange(coupon: CouponResponse): void {
    this.product.coupon_id = coupon ? coupon.id : null;
    if(this.selectedTab === TABS_TYPE.immediate) {
      this.product.end_of_term = false;
      this.isCouponChanged = {
        immediate: false,
        renewal: true
      };
    } else {
      this.product.end_of_term = true;
      this.isCouponChanged = {
        immediate: true,
        renewal: false
      };
    }
    this.getEstimate(LOADING_TYPE.lines, coupon);
  }
}